/**
 * Typed filter config utilities.
 *
 * Provides type-safe filter configuration where:
 * - Filter option values are preserved as literal types via `as const satisfies`
 * - Invalid props are caught at compile time via the FilterConfigMap constraint
 * - FilterValues<Config, Key> extracts the value union for a given search param
 * - toFilterColumns() / toFilterList() convert the object form to the shape
 *   `<TableFilters>` consumes (a left group, optionally paired with a right one)
 */

import { isValidEnumValue, parseLocalDateString, toLocalDateString } from '@bridge/utils';

import type { TableFilterProps } from './TableFilter';

// Distribute Omit over each member of a union (preserves variant-specific props)
type DistributeOmit<T, K extends string> = T extends unknown ? Omit<T, K> : never;

/**
 * A single filter entry: same union as TableFilterProps, but with `searchParam`
 * removed (it becomes the object key instead).
 *
 * Since the base filter props use `readonly FilterOption[]`, `as const` arrays
 * are directly compatible. Variants that don't accept `filterOptions` (input,
 * search) preserve their `filterOptions?: never` constraint.
 *
 * TODO: once all consumers use the typed config format (object keyed by search param name),
 * remove `searchParam` from the base filter prop interfaces entirely and drop
 * the DistributeOmit wrapper here.
 */
export type FilterEntryProps = DistributeOmit<TableFilterProps, 'searchParam'>;

/**
 * The filter config object shape: keys are search param names, values are filter props.
 *
 * Usage:
 * ```ts
 * const filterConfig = {
 *   status: {
 *     variant: 'select',
 *     filterOptions: [
 *       { value: 'active', label: 'Active' },
 *       { value: 'inactive', label: 'Inactive' },
 *     ],
 *   },
 * } as const satisfies FilterConfigMap;
 * ```
 */
export type FilterConfigMap = Record<string, FilterEntryProps>;

/**
 * Extracts the string value union for a given search param key from a filter config.
 *
 * - For filters with a declared `validValues`, returns that literal union — checked first so it
 *   matches `sanitizeSearchParams`' runtime priority (see that function's docstring).
 * - Otherwise, for filters with `filterOptions`, returns the union of option values (intersected
 *   with `string` to exclude `boolean | null`).
 * - For filters without either (e.g., input, search), returns `string`.
 *
 * Usage:
 * ```ts
 * type StatusValues = FilterValues<typeof filterConfig, 'status'>;
 * // → 'active' | 'inactive'
 * ```
 */
type BooleanToString<T> = T extends true ? 'true' : T extends false ? 'false' : T;

/** Extract the scalar element type from a filter option value — handles array types from array query params */
type FilterOptionElement<V> = V extends readonly (infer U)[]
  ? BooleanToString<U> & string
  : BooleanToString<V> & string;

export type FilterValues<T, K extends keyof T> = T[K] extends { validValues: readonly (infer V)[] }
  ? V & string
  : T[K] extends { filterOptions: readonly { value: infer V }[] }
    ? FilterOptionElement<V>
    : string;

/**
 * Infers the search params shape from a filter config.
 * Each key maps to its possible string values (from filterOptions) or string (for input filters).
 * MultiSelect filters return string[] instead of string.
 *
 * Usage:
 * ```ts
 * type Params = InferSearchParams<typeof filterConfig>;
 * // → { scope?: 'mine' | 'assigned_to_me' | 'all'; status?: 'draft' | 'pending_approval'; state?: TripState[]; }
 * ```
 */
export type InferSearchParams<T extends FilterConfigMap> = {
  [K in keyof T & string as T[K] extends { variant: 'dateRange' } ? never : K]?: T[K] extends {
    variant: 'multiSelect';
  }
    ? Extract<FilterValues<T, K>, string>[]
    : FilterValues<T, K>;
} & {
  [
    K in keyof T as T[K] extends {
      variant: 'dateRange';
      fromDateParam: infer F extends string;
      toDateParam: infer P extends string;
    }
      ? F | P
      : never
  ]?: string;
};

/** Well-known pagination search param names managed by PaginationFooter */
export interface PaginationSearchParams<TSortBy extends string = string> {
  nextCursor?: string;
  prevCursor?: string;
  sortBy?: TSortBy;
  sortDirection?: 'asc' | 'desc';
}

/**
 * Sanitizes raw URL search params against a filter config.
 *
 * - For filters with a declared `validValues` allow-list: only passes values on that list
 *   (takes priority over `filterOptions` — see `validValues`' own docstring)
 * - For filters with `filterOptions` (and no `validValues`): only passes values that match a
 *   declared option
 * - For filters without either (input, search): passes any string value
 * - For `dateRange` filters: passes their `fromDateParam`/`toDateParam` URL values
 * - For `date` filters: passes a real YYYY-MM-DD day, otherwise their `getDefaultValue` day (if any)
 * - Unknown keys (not in filterConfig) are dropped
 *
 * Also extracts well-known pagination params (`nextCursor`, `prevCursor`).
 * When `sortConfig` is provided, also validates and extracts `sortBy`/`sortDirection`.
 */
export function sanitizeSearchParams<T extends FilterConfigMap, TSortBy extends string = string>(
  filterConfig: T,
  rawSearchParams: Record<string, unknown> | undefined,
  sortConfig?: { sortKeyMap: Record<string, TSortBy> }
): { filterParams: InferSearchParams<T>; paginationParams: PaginationSearchParams<TSortBy> } {
  const filterParams: Record<string, string | string[]> = {};
  const paginationParams: PaginationSearchParams<TSortBy> = {};

  // Extract filter params: validate against filterConfig
  for (const [key, entry] of Object.entries(filterConfig)) {
    // Date range filters write to their fromDateParam/toDateParam URL keys, not the config key
    if ('variant' in entry && entry.variant === 'dateRange') {
      const fromValue = rawSearchParams?.[entry.fromDateParam];
      const toValue = rawSearchParams?.[entry.toDateParam];
      if (typeof fromValue === 'string') filterParams[entry.fromDateParam] = fromValue;
      if (typeof toValue === 'string') filterParams[entry.toDateParam] = toValue;
      continue;
    }

    const rawValue = rawSearchParams?.[key];

    if ('variant' in entry && entry.variant === 'date') {
      const day = typeof rawValue === 'string' ? parseLocalDateString(rawValue) : null;
      const resolved = day ?? entry.getDefaultValue?.();
      if (resolved) filterParams[key] = toLocalDateString(resolved);
      continue;
    }

    const isMultiSelect = 'variant' in entry && entry.variant === 'multiSelect';

    if (rawValue === undefined || rawValue === null || typeof rawValue !== 'string') {
      // No URL value — apply defaultValue if the filter declares one
      if ('defaultValue' in entry && Array.isArray(entry.defaultValue) && entry.defaultValue.length > 0) {
        filterParams[key] = isMultiSelect
          ? ([...entry.defaultValue] as string[])
          : (entry.defaultValue as string[]).join(',');
      }
      continue;
    }

    // A declared `validValues` allow-list takes priority over `filterOptions` — it exists
    // precisely to validate against a broader set than a dropdown narrowed for display
    // (e.g. `select` filters over a subset of a shared enum, or `custom` filters with no
    // `filterOptions` at all).
    if ('validValues' in entry && Array.isArray(entry.validValues) && entry.validValues.length > 0) {
      const allowed = new Set(entry.validValues as readonly string[]);

      if (isMultiSelect) {
        const validated = rawValue.split(',').filter((v) => allowed.has(v));
        if (validated.length > 0) {
          filterParams[key] = validated;
        }
      } else if (allowed.has(rawValue)) {
        filterParams[key] = rawValue;
      }
    } else if ('filterOptions' in entry && Array.isArray(entry.filterOptions) && entry.filterOptions.length > 0) {
      // Enum filter: validate value is one of the declared options
      const allowed = new Set(
        (entry.filterOptions as readonly { value: string | boolean | null }[]).map((o) => String(o.value))
      );
      // A default-checked checkbox records its uncheck as an explicit 'false', which its options
      // (typically just the labelled 'true') rarely declare. Accept the pair so the divergence
      // from the default isn't silently stripped.
      if ('variant' in entry && entry.variant === 'checkbox' && entry.defaultValue?.length) {
        allowed.add('true');
        allowed.add('false');
      }

      if (isMultiSelect) {
        // Multi-select: validate each comma-separated value, return as array
        const validated = rawValue.split(',').filter((v) => allowed.has(v));
        if (validated.length > 0) {
          filterParams[key] = validated;
        }
      } else {
        // Single-select: validate the single value
        if (allowed.has(rawValue)) {
          filterParams[key] = rawValue;
        }
      }
    } else {
      // Input/search/custom-without-allowlist: accept any string
      filterParams[key] = rawValue;
    }
  }

  // Extract pagination params
  if (rawSearchParams) {
    const nextCursor = rawSearchParams.nextCursor;
    const prevCursor = rawSearchParams.prevCursor;
    if (typeof nextCursor === 'string') paginationParams.nextCursor = nextCursor;
    if (typeof prevCursor === 'string') paginationParams.prevCursor = prevCursor;

    if (sortConfig) {
      const rawSortBy = rawSearchParams.sortBy;
      const rawSortDirection = rawSearchParams.sortDirection;
      const validSortValues = Object.values(sortConfig.sortKeyMap);
      if (typeof rawSortBy === 'string' && isValidEnumValue(rawSortBy, validSortValues)) {
        paginationParams.sortBy = rawSortBy;
      }
      if (typeof rawSortDirection === 'string' && (rawSortDirection === 'asc' || rawSortDirection === 'desc')) {
        paginationParams.sortDirection = rawSortDirection;
      }
    }
  }

  return { filterParams: filterParams as InferSearchParams<T>, paginationParams };
}

/**
 * The shape `<TableFilters>` consumes: a left group of filters, optionally paired
 * with a right group. `<TableFilters>` renders them as a left column and a right
 * column with space between — it is NOT a multi-row layout. Build one with
 * {@link toFilterColumns} (two groups) or {@link toFilterList} (single group).
 *
 * Sizing (whether filters grow to fill the row) is a presentational concern and
 * lives on `<TableFilters>`' `grow` prop, not here.
 */
export interface FilterColumnsConfig {
  left: TableFilterProps[];
  right?: TableFilterProps[];
}

/** A fully-populated two-group config — the {@link toFilterColumns} result, with `right` guaranteed. */
export type FilterColumns = Required<FilterColumnsConfig>;

function toEntries<T extends FilterConfigMap>(config: T, keys: readonly (keyof T)[]): TableFilterProps[] {
  return keys.map((key) => ({ ...config[key], searchParam: key as string }) as TableFilterProps);
}

/**
 * Splits a typed filter config into an explicit left and right group (filters
 * left, actions/reset right, space between). Injects `searchParam` from the
 * object key. Every config key must be assigned to exactly one group, and no key
 * may appear in both; a missing or duplicated key is a compile error.
 *
 * Usage:
 * ```ts
 * const filterConfig = { ... } as const satisfies FilterConfigMap;
 * const columns = toFilterColumns(filterConfig, { left: ['scope', 'status'], right: ['dateRange'] });
 * <TableFilters config={columns} filterType="client" />
 * ```
 */
export function toFilterColumns<
  T extends FilterConfigMap,
  const L extends readonly (keyof T)[],
  const R extends readonly Exclude<keyof T, L[number]>[],
>(
  config: T,
  columns: { left: L; right: R },
  ..._missing: [Exclude<keyof T, L[number] | R[number]>] extends [never]
    ? []
    : [error: `Missing keys: ${Exclude<keyof T & string, (L[number] | R[number]) & string>}`]
): FilterColumns {
  return { left: toEntries(config, columns.left), right: toEntries(config, columns.right) };
}

/**
 * Builds a single left group from a typed filter config. Injects `searchParam`
 * from the object key. With no `keys`, includes every filter in object order;
 * pass `keys` to select and order an explicit subset.
 *
 * Usage:
 * ```ts
 * const filterConfig = { ... } as const satisfies FilterConfigMap;
 * <TableFilters config={toFilterList(filterConfig)} filterType="client" />
 * ```
 */
export function toFilterList<T extends FilterConfigMap>(
  config: T,
  keys?: readonly (keyof T)[]
): FilterColumnsConfig {
  const left = keys
    ? toEntries(config, keys)
    : Object.entries(config).map(([key, props]) => ({ ...props, searchParam: key }) as TableFilterProps);
  return { left };
}
